/**
 * PipelineStageChip — compact status indicator for a single pipeline stage.
 *
 * Renders a small pill with an icon + label. Status drives the color and icon.
 * Clicking the chip opens the stage detail modal.
 */
import { clsx } from 'clsx';
import type { PipelineStatus } from '@/services/api';

interface Props {
  label: string;
  status: PipelineStatus;
  count?: number;   // e.g. number of pairs at this status for run-level view
  onClick?: () => void;
  size?: 'sm' | 'xs';
}

const STATUS_CONFIG: Record<
  PipelineStatus,
  { icon: string; classes: string }
> = {
  pending:   { icon: '○',  classes: 'bg-slate-100 text-slate-500 border-slate-200' },
  running:   { icon: '⟳',  classes: 'bg-blue-50 text-blue-600 border-blue-200 animate-pulse' },
  completed: { icon: '✓',  classes: 'bg-green-50 text-green-700 border-green-200' },
  skipped:   { icon: '⊘',  classes: 'bg-slate-50 text-slate-400 border-slate-100' },
  failed:    { icon: '✕',  classes: 'bg-red-50 text-red-600 border-red-200' },
  blocked:   { icon: '⊡',  classes: 'bg-amber-50 text-amber-600 border-amber-200' },
};

export function PipelineStageChip({ label, status, count, onClick, size = 'sm' }: Props) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.pending;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      title={`${label} — ${status}`}
      className={clsx(
        'inline-flex items-center gap-1 rounded-full border font-medium transition-opacity',
        cfg.classes,
        size === 'xs' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-xs',
        onClick ? 'cursor-pointer hover:opacity-80' : 'cursor-default',
      )}
    >
      <span aria-hidden>{cfg.icon}</span>
      <span className="truncate max-w-[80px]">{label}</span>
      {count !== undefined && count > 0 && (
        <span className="ml-0.5 opacity-70">×{count}</span>
      )}
    </button>
  );
}
